
import UIDrawer from "@/components/drawer/UIDrawer";
import UISideBar from "@/components/sidebar/UISideBar";
import colors from "@/utils/colors";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import UILoader from "@/components/loader/UILoader";
import React, { memo, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';

interface UIRightSidebarLayoutProps {
  children: React.ReactNode;
  drawerWidth: number;
  open: boolean;
  messageLoading?: string;
  titleLoading?: string;
  isLoading?: boolean;
  marginRight?: number;
  percentage?: number;
  isSlow?: boolean;
}
const UIRightSidebarLayout: React.FC<UIRightSidebarLayoutProps> = ({
  children, drawerWidth = 240, open, isLoading = false, messageLoading, titleLoading, marginRight, percentage, isSlow
}) => {
  const memoizedChildren = useMemo(() => children, [children]);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(1366));
  const [isHovered, setIsHovered] = useState(false);
  const [version, setVersion] = useState<string>("");
  
  // Ref para evitar múltiples llamadas a la API
  const hasFetchedVersion = useRef(false);

  // Memoizar el fetch de versión para evitar llamadas innecesarias
  const fetchVersion = useCallback(async () => {
    if (hasFetchedVersion.current) return;
    
    try {
      const res = await fetch('/api/version');
      const data = await res.json();
      setVersion(data.version);
      hasFetchedVersion.current = true;
    } catch (error) {
      console.error('Error fetching version:', error);
    }
  }, []);

  useEffect(() => {
    fetchVersion();
  }, [fetchVersion]);

  // Memoizar el cálculo del estado del drawer
  const getDrawerOpen = useCallback(() => {
    if (isSmallScreen && !isHovered) {
      return isHovered;
    }
    return open || isHovered;
  }, [isSmallScreen, isHovered, open]);

  // Memoizar los handlers de mouse para evitar recreaciones
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);
  // Memoizar los estilos para evitar recreaciones
  const containerStyles = useMemo(() => ({
    display: 'flex', 
    marginRight: marginRight + 'px', 
    backgroundColor: colors.blue4
  }), [marginRight]);

  const sidebarBoxStyles = useMemo(() => ({
    backgroundColor: colors.blue1, 
    height: "100vh"
  }), []);

  // Memoizar el estado del drawer para evitar cálculos innecesarios
  const drawerOpen = useMemo(() => getDrawerOpen(), [getDrawerOpen]);

  return (
    <>
      <Box sx={containerStyles}>
        <CssBaseline />
        <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <UIDrawer open={drawerOpen} drawerwidth={drawerWidth}>
            <Box sx={sidebarBoxStyles}>
              <UISideBar open={drawerOpen} version={version} />
            </Box>
          </UIDrawer>
        </div>
        {memoizedChildren}
      </Box>

      {isLoading && (
        <UILoader 
          open={isLoading} 
          loaderText={messageLoading} 
          loaderTextTitle={titleLoading} 
          percentage={percentage} 
          isSlow={isSlow} 
        />
      )}
    </>
  );
};

export default memo(UIRightSidebarLayout);
